import React, { useState } from 'react';

import styled from 'styled-components';

import EditDataModal from 'components/CommunityInfo/common/edit/EditDataModal';
import PhotosEdit from 'components/CommunityInfo/general/photos/edit/PhotosEdit';
import RateTrendsEdit from 'components/CommunityInfo/general/rateTrends/RateTrendsEdit';
import CommunityIncentivesModal from 'components/CommunityInfo/incentives/CommunityIncentivesModal';
import { INCENTIVE_MODAL_TYPE } from 'components/CommunityInfo/incentives/constants';
import { OWNERSHIP_TYPES } from 'constants/ownership';
import { isMysteryShopShopper } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { useEditShopRequestModalContext } from './EditShopRequestModalContext';
import { SHOP_REQUEST_STATUS_LABELS } from '../constants';

const Container = styled.div`
  padding: 20px;
`;

const EditLinksRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  color: var(--dark-blue);
  max-width: 272px;
`;

const Title = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
`;

type EditLinkProps = {
  disabled?: boolean;
};

const EditLink = styled.div<EditLinkProps>`
  cursor: ${(props) => `${props.disabled ? 'not-allowed' : 'pointer'}`};
  color: ${(props) => `${props.disabled ? 'var(--text-secondary)' : 'var(--dark-blue)'}`};
  font-size: 14px;
`;

const DotContainer = styled.span`
  margin: 0 0.25em;
`;

const Dot = () => <DotContainer>·</DotContainer>;

const EditShopRequestModalStaticData = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { shopRequest, originalShopRequest, setShopRequest } = useEditShopRequestModalContext();

  const [showEditPhotosModal, setShowEditPhotosModal] = useState(false);
  const [showEditRateTrendsModal, setShowEditRateTrendsModal] = useState(false);
  const [showEditIncentivesModal, setShowEditIncentivesModal] = useState(false);

  const disableView =
    isMysteryShopShopper(currentUser) &&
    [SHOP_REQUEST_STATUS_LABELS.APPROVED, SHOP_REQUEST_STATUS_LABELS.CANCELED].includes(
      originalShopRequest?.status || 0
    );
  const disableEdit =
    isMysteryShopShopper(currentUser) &&
    [SHOP_REQUEST_STATUS_LABELS.NOT_STARTED, SHOP_REQUEST_STATUS_LABELS.NEEDS_APPROVAL].includes(
      originalShopRequest?.status || 0
    );

  const openEditModal = (callback: (value: boolean) => void) => {
    if (!disableView) {
      callback(true);
    }
  };

  return (
    <Container>
      <Title>Edit static data</Title>
      <EditLinksRow>
        <EditLink
          disabled={disableView}
          onClick={() => openEditModal(setShowEditPhotosModal)}
          data-test-id="sr-modal-photos"
        >
          Photos
        </EditLink>
        <Dot />
        <EditLink
          disabled={disableView}
          onClick={() => openEditModal(setShowEditRateTrendsModal)}
          data-test-id="sr-modal-rate-trends"
        >
          Rate Trends
        </EditLink>
        <Dot />
        <EditLink
          disabled={disableView}
          onClick={() => openEditModal(setShowEditIncentivesModal)}
          data-test-id="sr-modal-incentives"
        >
          Incentives
        </EditLink>
      </EditLinksRow>
      {showEditPhotosModal && (
        <EditDataModal
          open={showEditPhotosModal}
          title={'Edit Photos'}
          communityName={shopRequest?.community.comunity_name}
          onClose={() => setShowEditPhotosModal(false)}
          onSuccess={() => setShowEditPhotosModal(false)}
        >
          <PhotosEdit community={shopRequest?.community} disabled={disableEdit} />
        </EditDataModal>
      )}
      {showEditRateTrendsModal && (
        <EditDataModal
          open={showEditRateTrendsModal}
          title={'Edit Rate Trends'}
          communityName={shopRequest?.community.comunity_name}
          onClose={() => setShowEditRateTrendsModal(false)}
          onSuccess={() => setShowEditRateTrendsModal(false)}
        >
          <RateTrendsEdit community={shopRequest?.community} disabled={disableEdit} />
        </EditDataModal>
      )}
      {showEditIncentivesModal && (
        <CommunityIncentivesModal
          openModal={showEditIncentivesModal}
          modalType={INCENTIVE_MODAL_TYPE.CREATE}
          ownershipType={OWNERSHIP_TYPES.SHARED}
          onClose={() => setShowEditIncentivesModal(false)}
          community={shopRequest?.community}
          disabled={disableEdit}
          shopRequestId={shopRequest?.id}
        />
      )}
    </Container>
  );
};

export default EditShopRequestModalStaticData;
